import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useCart } from './context/CartContext';
import http from './api/http';

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 10;

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const orderId = sp.get('order');
  const { clear } = useCart();

  const [status, setStatus] = useState('pending'); // pending | paid | timeout | error
  const [attempts, setAttempts] = useState(0);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timerId;

    async function poll(nextAttempt = 1) {
      if (!orderId || cancelled) return;

      setIsChecking(true);
      setAttempts(nextAttempt);

      try {
        const { data } = await http.get(`/api/orders/${orderId}`, {
          headers: { 'Cache-Control': 'no-cache' },
          params: { t: Date.now() },
        });

        if (cancelled) return;

        if (data?.payment?.status === 'paid' || ['paid', 'assembling', 'shipped', 'delivered', 'completed'].includes(data?.status)) {
          clear();
          try { sessionStorage.removeItem('lavsan_draft_order'); } catch { /* ignore */ }
          setStatus('paid');
          return;
        }

        if (nextAttempt >= MAX_ATTEMPTS) {
          setStatus('timeout');
          return;
        }

        setStatus('pending');
        timerId = setTimeout(() => poll(nextAttempt + 1), POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;

        if (nextAttempt >= MAX_ATTEMPTS) {
          setStatus('error');
          return;
        }

        setStatus('pending');
        timerId = setTimeout(() => poll(nextAttempt + 1), POLL_INTERVAL_MS);
      } finally {
        if (!cancelled) {
          setIsChecking(false);
        }
      }
    }

    poll(1);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [orderId, clear]);

  if (status === 'paid') {
    return (
      <div className="page container">
        <h1>Оплата прошла</h1>
        <p>Спасибо за заказ. Кассовый чек будет направлен на телефон и/или e‑mail.</p>
        <p>
          Сохраните{' '}
          <Link to="/return-memo" target="_blank" rel="noreferrer">
            памятку о порядке и сроках возврата
          </Link>
          {' '}(можно распечатать). Она также будет предоставлена при передаче товара.
        </p>
        <p><Link to="/account/orders">Мои заказы</Link></p>
        <p><Link to="/catalog">В каталог</Link></p>
      </div>
    );
  }

  if (!orderId) {
    return (
      <div className="page container">
        <h1>Не найден номер заказа</h1>
        <p><Link to="/account/orders">Мои заказы</Link></p>
      </div>
    );
  }

  return (
    <div className="page container">
      <h1>
        {status === 'timeout'
          ? 'Платёж ещё обрабатывается'
          : status === 'error'
            ? 'Не удалось проверить статус платежа'
            : 'Проверяем оплату'}
      </h1>
      <p>
        {status === 'error'
          ? 'Не удалось проверить оплату. Попробуйте чуть позже в разделе заказов.'
          : `Обычно это занимает несколько секунд. Попытка ${Math.min(attempts, MAX_ATTEMPTS)} из ${MAX_ATTEMPTS}.`}
      </p>
      {isChecking && <p>Обновляем статус…</p>}
      <p><Link to="/account/orders">Мои заказы</Link></p>
      <p><Link to="/catalog">Вернуться в каталог</Link></p>
    </div>
  );
}
